import React, {useState, useMemo, useRef} from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {SlideFade} from '@chakra-ui/react'

// Chakra Components
import {
    useDisclosure,
    Button,
    Box,
    Flex,
    Stack,
    Skeleton,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import EmptyCart from '../../pages/cart/partials/empty-cart'
import ProductItem from '../../components/product-item'
import {
    CartRemoveButton,
    CartEditButton,
    CartAddToWishlistButton
} from '../../pages/cart/partials/cart-secondary-button-group'

// Hooks
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Constants
import {
    API_ERROR_MESSAGE,
    TOAST_ACTION_VIEW_WISHLIST,
    TOAST_MESSAGE_ALREADY_IN_WISHLIST
} from '@salesforce/retail-react-app/app/constants'

// Utilities
import debounce from 'lodash/debounce'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment,
    useProducts,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import CartEditQuickViewDrawer from '../cart-edit-quick-view-drawer'
import BundleProductViewModal from '../product-view-modal/bundle'

import {useCustomToast} from '../../contexts/custom-toast-context'

import {sendItemCartEvent, sendWishlistEvent} from '../../utils/gtm-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getSiteEnvVarValue} from '../../utils/site-config'

const DEBOUNCE_WAIT = 750
const ProductItemList = ({
    setHasFinalSaleInCart,
    isMiniCart,
    hideRemoveAction,
    hideWishlistAction,
    basketData,
    variant,
    showFewItemsLeft = false
}) => {
    const {showCustomToast, removeToast} = useCustomToast()
    const {data: currentBasket, isLoading: isCurrentBasketLoading} = useCurrentBasket()
    const basket = basketData || currentBasket
    const isLoading = basketData ? false : isCurrentBasketLoading
    const productIds = basket?.productItems?.map(({productId}) => productId).join(',') ?? ''
    const {data: products, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds,
                allImages: false,
                perPricebook: true
            }
        },
        {
            enabled: Boolean(productIds),
            staleTime: 5 * 60 * 1000,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    const config = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const configFinalSaleLabel = getSiteEnvVarValue(config.app, 'ENABLE_FINAL_SALE_LABEL', siteId) === 'true'

    const {data: customer} = useCurrentCustomer()
    const {customerId, isRegistered} = customer

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    const {data: bundleChildProductData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds?.join(','),
                allImages: false,
                expand: ['availability', 'variations'],
                select: '(data.(id,inventory))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true,
            select: (result) => {
                return result?.data?.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})
            }
        }
    )

    // We use the `products` object to reference products by itemId instead of productId
    // Since with product bundles, even though the parent productId is the same,
    // variant selection of the bundle children can be different,
    // and require unique references to each product bundle
    const productsByItemId = useMemo(() => {
        const updateProductsByItemId = {}
        let hasFinalSale = false

        basket?.productItems?.forEach((productItem) => {
            let currentProduct = products?.[productItem?.productId]

            // calculate inventory for product bundles based on availability of children
            if (productItem?.bundledProductItems && bundleChildProductData) {
                let lowestStockLevel =
                    currentProduct?.inventory?.stockLevel ?? Number.MAX_SAFE_INTEGER
                let productWithLowestInventory = ''
                productItem?.bundledProductItems.forEach((bundleChild) => {
                    const bundleChildStockLevel =
                        bundleChildProductData?.[bundleChild.productId]?.inventory?.stockLevel ??
                        Number.MAX_SAFE_INTEGER
                    lowestStockLevel = Math.min(lowestStockLevel, bundleChildStockLevel)
                    if (lowestStockLevel === bundleChildStockLevel)
                        productWithLowestInventory = bundleChild.productName
                })

                if (currentProduct?.inventory) {
                    currentProduct = {
                        ...currentProduct,
                        inventory: {
                            ...currentProduct.inventory,
                            stockLevel: lowestStockLevel,
                            lowestStockLevelProductName: productWithLowestInventory
                        }
                    }
                }
            }

            let enableFinalSaleLabel = configFinalSaleLabel || currentProduct?.c_isEGiftCard || currentProduct?.c_isGiftCard

            // Check for final sale products
            if (currentProduct?.c_isFinalSale && enableFinalSaleLabel) {
                hasFinalSale = true
            }

            updateProductsByItemId[productItem.itemId] = currentProduct
        })

        // Set the hasFinalSaleInCart state
        setHasFinalSaleInCart && setHasFinalSaleInCart(hasFinalSale)

        return updateProductsByItemId
    }, [basket, products, bundleChildProductData, setHasFinalSaleInCart])

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const addItemToBasketMutation = useShopperBasketsMutation('addItemToBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)
    const [removingItemIds, setRemovingItemIds] = useState(new Set())
    const removedItemsCacheRef = useRef({})

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const modalProps = useDisclosure()

    /******************* Shipping Methods for basket shipment *******************/
    // do this action only if the basket shipping method is not defined
    // we need to fetch the shippment methods to get the default value before we can add it to the basket
    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me'
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket.shipments.length > 0 &&
                !basket.shipments[0].shippingMethod,
            onSuccess: (data) => {
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: 'me'
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    /************************* Error handling ***********************/
    const showError = () => {
        showCustomToast({
            message: formatMessage(API_ERROR_MESSAGE)
        })
    }
    /************************* Error handling ***********************/

    /**************** Wishlist ****************/
    const {data: wishlist} = useWishList()

    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )
    const [isRedirectingAfterWishlistMove, setIsRedirectingAfterWishlistMove] = useState(false)

    const handleAddToWishlist = async (product) => {
        try {
            if (!customerId || !wishlist) {
                return
            }

            const isItemInWishlist = wishlist?.customerProductListItems?.find(
                (i) => i.productId === product?.id
            )

            const isMaximumNumberOfItemsReached = wishlist?.customerProductListItems?.length >= 100

            if (!isItemInWishlist) {
                if (isMaximumNumberOfItemsReached) {
                    showCustomToast({
                        message: formatMessage(
                            {
                                id: 'cart.info.full_message',
                                defaultMessage:
                                    'You have reached the maximum number of items for your Wishlist. <a>See Wishlist</a>'
                            },
                            {
                                a: (chunks) => (
                                    <Link
                                        href="/account/wishlist"
                                        display="block"
                                        mt={2}
                                        textDecoration="underline"
                                    >
                                        {chunks}
                                    </Link>
                                )
                            }
                        )
                    })
                } else {
                    const createdWishlistItem = await createCustomerProductListItem.mutateAsync({
                        parameters: {
                            listId: wishlist.id,
                            customerId
                        },
                        body: {
                            // NOTE: APi does not respect quantity, it always adds 1
                            quantity: product.quantity,
                            productId: product.productId,
                            public: false,
                            priority: 1,
                            type: 'product'
                        }
                    })

                    const basketItem = basket?.productItems?.find(
                        (item) =>
                            item.itemId === product?.itemId ||
                            item.productId === product?.id ||
                            item.productId === product?.productId
                    )
                    const isLastBasketItem = (basket?.productItems?.length || 0) === 1

                    // GTM Modification
                    sendWishlistEvent(product, false)

                    if (basketItem?.itemId) {
                        await removeItemFromBasketMutation.mutateAsync({
                            parameters: {
                                basketId: basket?.basketId,
                                itemId: basketItem.itemId
                            }
                        })
                    }

                    const createdWishlistItemId =
                        createdWishlistItem?.id || createdWishlistItem?.itemId
                    const toastId = showCustomToast({
                        message: formatMessage({
                            id: 'cart.info.added_to_wishlist',
                            defaultMessage: 'Added to wishlist'
                        }),
                        actionLabel: formatMessage({
                            id: 'cart_minicart.button.undo',
                            defaultMessage: 'Undo'
                        }),
                        onActionClick: async () => {
                            removeToast(toastId)
                            try {
                                if (basket?.basketId) {
                                    await addItemToBasketMutation.mutateAsync({
                                        parameters: {basketId: basket.basketId},
                                        body: [
                                            {
                                                productId:
                                                    basketItem?.productId || product?.productId,
                                                quantity:
                                                    basketItem?.quantity || product?.quantity || 1,
                                                ...(basketItem?.optionItems?.length > 0 && {
                                                    optionItems: basketItem.optionItems
                                                })
                                            }
                                        ]
                                    })
                                }

                                if (
                                    createdWishlistItemId &&
                                    wishlist?.id &&
                                    customerId
                                ) {
                                    await deleteCustomerProductListItem.mutateAsync({
                                        parameters: {
                                            listId: wishlist.id,
                                            customerId,
                                            itemId: createdWishlistItemId
                                        }
                                    })
                                }
                            } catch {
                                showError()
                            }
                        }
                    })

                    if (isLastBasketItem && variant === 'checkout-order-summary') {
                        setIsRedirectingAfterWishlistMove(true)
                        navigate('/cart')
                    }
                }
            } else {
                showCustomToast({
                    message: formatMessage(TOAST_MESSAGE_ALREADY_IN_WISHLIST),
                    actionLabel: formatMessage(TOAST_ACTION_VIEW_WISHLIST),
                    onActionClick: () => navigate('/account/wishlist')
                })
            }
        } catch {
            showError()
        }
    }
    /**************** Wishlist ****************/

    const handleUpdateBundle = async (bundle, bundleQuantity, childProducts) => {
        // close the modal before handle the change
        onClose()

        try {
            setCartItemLoading(true)
            const itemsToBeUpdated = getUpdateBundleChildArray(bundle, childProducts)

            // We only update the parent bundle when the quantity changes
            // Since top level bundles don't have variants
            if (bundle.quantity !== bundleQuantity) {
                itemsToBeUpdated.unshift({
                    itemId: bundle.itemId,
                    productId: bundle.productId,
                    quantity: bundleQuantity
                })
            }

            if (itemsToBeUpdated.length) {
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: basket.basketId
                    },
                    body: itemsToBeUpdated
                })
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId]
        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
        setCartItemLoading(true)
        setSelectedItem(product)

        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    setLocalQuantity({...localQuantity, [product.itemId]: undefined})
                },
                onError: () => {
                    // reset the quantity to the previous value
                    setLocalQuantity({...localQuantity, [product.itemId]: previousQuantity})
                    showError()
                }
            }
        )
    }, DEBOUNCE_WAIT)

    const handleChangeItemQuantity = async (product, value) => {
        const stockLevel = productsByItemId?.[product.itemId]?.inventory?.stockLevel ?? 1

        // Handle removing of the items when 0 is selected.
        if (value === 0) {
            // Flush last call to keep ui in sync with data.
            changeItemQuantity.flush()

            // Set the selected item to the current product to the modal acts on it.
            setSelectedItem(product)

            // Show the modal.
            modalProps.onOpen()

            // Return false as 0 isn't valid section.
            return false
        }

        // Cancel any pending handlers.
        changeItemQuantity.cancel()

        // Allow use to selected values above the inventory.
        if (value > stockLevel || value === product.quantity) {
            return true
        }

        // Take action.
        changeItemQuantity(value, product)

        return true
    }
    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket.basketId, itemId: product.itemId}
            },
            {
                onSettled: () => {
                    // GTM Modification
                    sendItemCartEvent(productsByItemId[product.itemId], true)
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                }
            }
        )
    }

    /********* Show Cart removal toast with immediate removal and undo **********/
    const cartRemoveToast = (product) => {
        // Cache product data so we can restore it on undo and keep it visible during animation
        removedItemsCacheRef.current[product.itemId] = product

        // Trigger SlideFade exit animation immediately
        setRemovingItemIds((prev) => {
            const next = new Set(prev)
            next.add(product.itemId)
            return next
        })

        // Remove from basket immediately
        handleRemoveItem(product)

        const cleanUp = (itemId) => {
            setRemovingItemIds((prev) => {
                const next = new Set(prev)
                next.delete(itemId)
                return next
            })
            delete removedItemsCacheRef.current[itemId]
        }

        const toastId = showCustomToast({
            message: formatMessage({
                id: 'cart.info.item_removed',
                defaultMessage: 'Removed from your bag'
            }),
            actionLabel: formatMessage({
                id: 'cart_minicart.button.undo',
                defaultMessage: 'Undo'
            }),
            onActionClick: async () => {
                removeToast(toastId)
                try {
                    const cachedProduct = removedItemsCacheRef.current[product.itemId]
                    await addItemToBasketMutation.mutateAsync({
                        parameters: {basketId: basket.basketId},
                        body: [
                            {
                                productId: product.productId,
                                quantity: cachedProduct?.quantity ?? product.quantity,
                                ...(product.optionItems?.length > 0 && {
                                    optionItems: product.optionItems
                                })
                            }
                        ]
                    })
                } catch {
                    showError()
                } finally {
                    cleanUp(product.itemId)
                }
            },
            onClose: () => {
                cleanUp(product.itemId)
            }
        })
    }

    /********* Build display list (basket items + items animating out after removal) **********/
    const displayItems = useMemo(() => {
        const basketItems = [...(basket?.productItems ?? [])].reverse()
        const basketItemIds = new Set(basketItems.map((i) => i.itemId))

        // Keep animating items visible in the list until animation/toast completes
        const animatingItems = []
        removingItemIds.forEach((itemId) => {
            const cached = removedItemsCacheRef.current[itemId]
            if (cached && !basketItemIds.has(itemId)) {
                animatingItems.push(cached)
            }
        })

        return [...basketItems, ...animatingItems]
    }, [basket?.productItems, removingItemIds])

    /********* Rendering  UI **********/
    if (isRedirectingAfterWishlistMove) {
        return null
    }

    if (isLoading || isProductsLoading) {
        const itemCount = basket?.productItems?.length || 3
        return (
            <>
                {Array.from({length: itemCount}).map((_, i) => (
                    <Skeleton
                        key={i}
                        height="180px"
                        borderRadius="md"
                        mb={{base: '10px', lg: '14px'}}
                    />
                ))}
            </>
        )
    }

    if (!isLoading && !displayItems.length) {
        return <EmptyCart isRegistered={isRegistered} />
    }

    return (
        <>
            {displayItems.map((productItem, idx) => {
                const isLastItem = idx === displayItems.length - 1
                const mergedProduct = {
                    ...productItem,
                    ...(productsByItemId && productsByItemId[productItem.itemId]),
                    isProductUnavailable: !isProductsLoading
                        ? !productsByItemId?.[productItem.itemId]
                        : undefined,
                    price: productItem.price,
                    priceAfterItemDiscount: productItem.priceAfterItemDiscount,
                    priceAfterOrderDiscount: productItem.priceAfterOrderDiscount,
                    basePrice: productItem.basePrice,
                    quantity: localQuantity[productItem.itemId]
                        ? localQuantity[productItem.itemId]
                        : productItem.quantity
                }
                const shouldShowFewItemsLeft = Boolean(
                    showFewItemsLeft &&
                        mergedProduct?.c_fewItemsLeft &&
                        mergedProduct?.inventory?.orderable &&
                        !mergedProduct?.c_isEGiftCard &&
                        !mergedProduct?.c_isGiftCard
                )
                return (
                    <SlideFade
                        key={productItem.itemId}
                        in={!removingItemIds.has(productItem.itemId)}
                        unmountOnExit
                        offsetY="20px"
                    >
                    <ProductItem
                        key={productItem.itemId}
                        index={idx}
                        removeAction={
                            !hideRemoveAction && (
                                <CartRemoveButton
                                    productName={productItem?.productName}
                                    cartRemoveToast={() => cartRemoveToast(productItem)}
                                    testId={isMiniCart ? 'mini-cart-button-remove' : 'cart-remove-button'}
                                />
                            )
                        }
                        editAction={
                            !productItem?.c_isEGiftCard &&
                            !productItem?.c_isGiftCard &&
                            !isMiniCart && (
                                <CartEditButton
                                    productName={productItem?.productName}
                                    onEditClick={(product) => {
                                        setSelectedItem(product)
                                        onOpen()
                                    }}
                                />
                            )
                        }
                        wishlistAction={
                            !hideWishlistAction && (
                                <CartAddToWishlistButton onAddToWishlistClick={handleAddToWishlist} />
                            )
                        }
                        product={mergedProduct}
                        isFewItemsLeft={shouldShowFewItemsLeft}
                        onItemQuantityChange={handleChangeItemQuantity.bind(this, productItem)}
                        showLoading={
                            isCartItemLoading && selectedItem?.itemId === productItem.itemId
                        }
                        handleRemoveItem={handleRemoveItem}
                        isMiniCart={isMiniCart}
                        variant={variant}
                        isLastItem={isLastItem}
                    />
                    </SlideFade>
                )
            })}

            {isOpen && (
                <Box>
                    {!selectedItem?.bundledProductItems && selectedItem && (
                        <CartEditQuickViewDrawer
                            isOpen={isOpen}
                            onClose={onClose}
                            selectedItem={selectedItem}
                            onUpdateCart={() => {
                                setCartItemLoading(false)
                                setSelectedItem(undefined)
                            }}
                        />
                    )}
                    {selectedItem?.bundledProductItems && (
                        <BundleProductViewModal
                            isOpen={isOpen}
                            onOpen={onOpen}
                            onClose={onClose}
                            product={selectedItem}
                            updateCart={(product, quantity, childProducts) =>
                                handleUpdateBundle(product, quantity, childProducts)
                            }
                        />
                    )}
                </Box>
            )}
        </>
    )
}

ProductItemList.propTypes = {
    isMiniCart: PropTypes.bool,
    hideRemoveAction: PropTypes.bool,
    hideWishlistAction: PropTypes.bool,
    basketData: PropTypes.object,
    setHasFinalSaleInCart: PropTypes.func,
    variant: PropTypes.string,
    showFewItemsLeft: PropTypes.bool,
    isLastItem: PropTypes.bool
}

export default ProductItemList
